import * as THREE from 'three';

class TextureCache {
  constructor() {
    this.cache = new Map();
    this.loader = new THREE.TextureLoader();
    this.loadingPromises = new Map();
    this.listeners = new Set();
  }

  /**
   * How many texture fetches are in flight right now.
   *
   * Exists so a UI can tell "the write landed" from "you can see it": a
   * Design Center pick is not finished when the room doc is written, it is
   * finished when the pixels are on the wall, and the gap between those two is
   * one network fetch of an image the browser has never seen. A cached texture
   * resolves from `cache` without ever touching this, which is exactly right —
   * re-picking a tile you have already used should show no wait at all.
   */
  pendingCount() {
    return this.loadingPromises.size;
  }

  /**
   * Notified whenever the in-flight count changes. Returns an unsubscribe.
   *
   * Deliberately a plain observer on the singleton rather than state threaded
   * down from the viewer: the canvas is six prop hops from the panel and every
   * hop names its props by hand, so a value routed that way arrives
   * `undefined` if any one of them forgets it (see web-app/CLAUDE.md).
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    for (const listener of this.listeners) {
      try {
        listener(this.pendingCount());
      } catch (error) {
        console.error('[textureCache] listener failed:', error);
      }
    }
  }

  async loadTexture(url) {
    // Return cached texture if available
    if (this.cache.has(url)) {
      const cachedTexture = this.cache.get(url);
      // Clone the texture to allow independent repeat settings per usage
      return cachedTexture.clone();
    }

    // If already loading this URL, wait for the existing promise
    if (this.loadingPromises.has(url)) {
      const texture = await this.loadingPromises.get(url);
      return texture.clone();
    }

    // Create loading promise
    const loadingPromise = new Promise((resolve, reject) => {
      this.loader.load(
        url,
        (texture) => {
          // Tag as sRGB so the renderer applies the correct inverse-gamma at
          // sample time. All texture images we load are color/diffuse maps
          // (PNG/JPG from the catalog) — they're sRGB-encoded at rest, and
          // skipping this tag causes washed-out / lighter rendering. Clones
          // returned from the cache inherit this property automatically.
          // If we ever load data textures (normal/roughness/metalness/AO) via
          // this cache, that's the point to introduce an options arg.
          texture.colorSpace = THREE.SRGBColorSpace;
          // Store the master texture in cache
          this.cache.set(url, texture);
          this.loadingPromises.delete(url);
          this.notify();
          resolve(texture);
        },
        undefined,
        (error) => {
          console.error('Error loading texture from:', url, error);
          this.loadingPromises.delete(url);
          this.notify();
          reject(error);
        }
      );
    });

    this.loadingPromises.set(url, loadingPromise);
    this.notify();

    try {
      const texture = await loadingPromise;
      return texture.clone();
    } catch (error) {
      console.error(`Failed to load texture from ${url}:`, error);
      throw error;
    }
  }

  // Clear specific texture from cache
  disposeTexture(url) {
    if (this.cache.has(url)) {
      const texture = this.cache.get(url);
      texture.dispose();
      this.cache.delete(url);
    }
  }

  // Clear all cached textures
  clearCache() {
    this.cache.forEach((texture) => {
      texture.dispose();
    });
    this.cache.clear();
    this.loadingPromises.clear();
    this.notify();
  }

  // Get cache statistics
  getCacheStats() {
    return {
      size: this.cache.size,
      urls: Array.from(this.cache.keys()),
      loading: Array.from(this.loadingPromises.keys()),
    };
  }
}

// Create singleton instance
const textureCache = new TextureCache();

export default textureCache;
